/**
 * Map the Casino Ad Service campaign shape onto the FE `Campaign` (the browser-card
 * model in `mock-data/types.ts`). Pure, so it unit-tests directly.
 *
 * The ad-service is leaner than the rich mock card: it has no `category` (these are
 * acquisition campaigns, so we tag them `acquisition` to keep the category filter
 * working), no card thumbnail on the list (we read one from the campaign DETAIL's
 * generated banners), and no `ended` status (that is derived from `end_date` by
 * `utils/campaign-status`). The card does not render channels, so `channels` is left
 * empty.
 *
 * Its status vocabulary does not line up with the studio's either, so the mapping is
 * partial: only `Running` and `Paused` become cards. Everything else (`Starting`,
 * `Failed`, `Deleted`, anything unrecognised) maps to null and is dropped from the UI
 * entirely, so the service never yields a `suggestion`. See {@link toCampaignStatus}.
 */
import type { Campaign } from '../../mock-data/types';
import {
  type AdCampaignAssets,
  type AdCampaignDetail,
  type AdCampaignSummary,
  resolveCampaignName,
  toCampaignStatus,
} from './api/casino-ad-service';

/** Banner sizes preferred for the card thumbnail, best card fit first. */
const THUMBNAIL_SIZE_PREFERENCE = ['300x250', '728x90', '300x100', '160x600', '300x50'];

/** A banner URL for the card thumbnail: the preferred size, else any, else empty. */
export const pickThumbnail = (assets: AdCampaignAssets): string => {
  for (const size of THUMBNAIL_SIZE_PREFERENCE) {
    const url = assets[size]?.[0]?.url;
    if (url) return url;
  }
  for (const variants of Object.values(assets)) {
    const url = variants?.[0]?.url;
    if (url) return url;
  }
  return '';
};

const formatDate = (epochSeconds: number): string =>
  new Date(epochSeconds * 1000).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

/** The card's "Campaign period" string from the epoch-SECONDS start / end. */
export const formatCampaignPeriod = (startSeconds: number, endSeconds: number | null): string =>
  endSeconds != null ? `${formatDate(startSeconds)} to ${formatDate(endSeconds)}` : `From ${formatDate(startSeconds)}`;

/**
 * Build a browser-card `Campaign` from a list summary, or `null` when the studio does
 * not show that campaign at all (`Starting` / `Failed` / `Deleted` / an unrecognised
 * state, see {@link toCampaignStatus}) — there is then no card to build.
 *
 * The card image is the campaign's `master_key_art` (carried on the list row), falling
 * back to a generated banner from the optional detail only when the master art is absent.
 * `expiresAt` is the end deadline in epoch MS (the FE convention); omitted for an
 * open-ended campaign so it never auto-ends.
 */
export const toCampaign = (summary: AdCampaignSummary, detail?: AdCampaignDetail): Campaign | null => {
  const status = toCampaignStatus(summary.status);
  if (status == null) return null;
  return {
    id: summary.campaign_id,
    title: resolveCampaignName(summary.campaign_name),
    image: summary.master_key_art || (detail ? pickThumbnail(detail.assets) : ''),
    period: formatCampaignPeriod(summary.start_date, summary.end_date),
    status,
    category: 'acquisition',
    // Only running/paused campaigns reach this point, so `expiresAt` is always the
    // run deadline the expiry rule turns into `ended` — never a suggestion's offer
    // countdown, which is a different thing the same field means on a suggestion card.
    ...(summary.end_date != null ? { expiresAt: summary.end_date * 1000 } : {}),
    channels: [],
  };
};

/**
 * The browser pool for a campaign list: every summary the studio can render, in the
 * order the service returned them. Campaigns in an unrenderable provider state are
 * dropped here, so they never reach a tab, a count or a card.
 */
export const toCampaignPool = (summaries: AdCampaignSummary[]): Campaign[] =>
  summaries.map((summary) => toCampaign(summary)).filter((campaign): campaign is Campaign => campaign != null);
